import * as Sentry from '@sentry/react';
import { type ReactNode, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router';
import { z } from 'zod';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useLogger from '@/hooks/use-logger';
import { simpleLogger } from '@/services/sentry';
import { BUILD_ENV } from '@/utils/constants';

const errorSchema = z.object({
  status: z.number(),
  data: z.string().nullish(),
});

export default function AppErrorLayout() {
  const logger = useLogger('Router');
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(error);
    logger.current.fatal(error);
  }, [error, logger]);

  const parsedError = errorSchema.safeParse(error);

  let node: ReactNode;
  if (!parsedError.success) {
    node = <GenericErrorPage error={error} />;
  } else {
    switch (parsedError.data.status) {
      case 401:
        node = <NotAuthenticatedPage />;
        break;
      case 404:
        node = <NotFoundPage />;
        break;
    }
  }

  return (
    <>
      <Helmet>
        <meta
          name='theme-color'
          content='#000000'
        />
      </Helmet>
      <div className='flex flex-1 h-screen-safe w-screen flex-center flex-col gap-4 pt-safe-offset-0 bg-gradient text-primary-content'>
        {node}
      </div>
    </>
  );
}

function GenericErrorPage({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const { dispatch } = useCommDispatcher();
  const navigate = useNavigate();

  const handleGoHome = () => {
    if (BUILD_ENV === 'embedded') {
      dispatch({ event: 'reload', data: { cause: 'error-page' } });
      simpleLogger({
        name: 'RELOAD',
        op: 'comm',
        requestAttributes: {
          cause: 'error',
          error: JSON.stringify(error),
        },
      });
    } else {
      navigate('/', { replace: true });
    }
  };

  return (
    <>
      <h2 className='text-6xl font-extrabold'>{t('errors.error')}</h2>

      <pre className='text-xs font-mono max-w-screen-sm break-all'>{JSON.stringify(error)}</pre>

      <button
        type='button'
        onClick={handleGoHome}
        className='btn btn-secondary btn-outline mt-12'>
        {t('common.go-home')}
      </button>
    </>
  );
}

function NotFoundPage() {
  const { t } = useTranslation();
  const { dispatch } = useCommDispatcher();
  const navigate = useNavigate();

  const handleGoHome = () => {
    if (BUILD_ENV === 'embedded') {
      dispatch({ event: 'reload', data: { cause: 'not-found-page' } });
      simpleLogger({
        name: 'RELOAD',
        op: 'comm',
        requestAttributes: {
          cause: 'not-found',
        },
      });
    } else {
      navigate('/', { replace: true });
    }
  };

  return (
    <>
      <h2 className='text-6xl font-extrabold'>404</h2>
      <h3 className='text-lg'>{t('errors.not-found')}</h3>

      <button
        type='button'
        onClick={handleGoHome}
        className='btn btn-secondary btn-outline mt-12'>
        {t('common.go-home')}
      </button>
    </>
  );
}

function NotAuthenticatedPage() {
  const { t } = useTranslation();
  const { dispatch } = useCommDispatcher();
  const navigate = useNavigate();

  const handleGoHome = () => {
    if (BUILD_ENV === 'embedded') {
      dispatch({ event: 'reload', data: { cause: 'unauthenticated-page' } });
      simpleLogger({
        name: 'RELOAD',
        op: 'comm',
        requestAttributes: {
          cause: 'unauthenticated',
        },
      });
    } else {
      navigate('/', { replace: true });
    }
  };

  return (
    <>
      <h3 className='text-lg'>{t('errors.not-authenticated')}</h3>

      <button
        type='button'
        onClick={handleGoHome}
        className='btn btn-secondary btn-outline mt-12'>
        {t('common.go-home')}
      </button>
    </>
  );
}
